import React, { useRef, useEffect, useState } from 'react'
import { FontAwesomeIcon }                    from '@fortawesome/react-fontawesome'
import { jsRequest }                          from "@react/helpers/fetch"
import { postRequest, pathname }              from '@helpers/javascript/javascript'
import { workorderModalPath }                 from '@helpers/javascript/calendar_helper'
import { useCalendar }                        from './useCalendar'

const EventCard = ({
  info,
  showDate,
  showDuration,
  externalCard,
  onRemoveBooking
}) => {
  const eventRef = useRef(null)
  const SEPARATED = "separated"
  const [showMarker, setShowMarker] = useState(false)

  // Route optimizer padlock — closed = excluded from the optimizer.
  const [locked, setLocked]   = useState(!!info.fixed_planning)
  const [locking, setLocking] = useState(false)
  useEffect(() => { setLocked(!!info.fixed_planning) }, [info.fixed_planning])

  const { setState, TOOLS, setFilter } = useCalendar()

  // The card lives inside a draggable FullCalendar event: swallow the press
  // itself, or the click would start a drag / open the workorder.
  const swallow = e => { e.preventDefault(); e.stopPropagation() }

  const toggleLock = e => {
    swallow(e)
    if (locking) return

    setLocking(true)
    const next = !locked
    setLocked(next) // optimistic: the padlock must feel instant
    postRequest({
      url:  pathname({ path: '/calendar/toggle_fixed_planning', format: 'json' }),
      body: { workorder_id: info.workorder_id },
    }).then(res => {
      if (res && typeof res.fixed_planning === 'boolean') setLocked(res.fixed_planning)
      else setLocked(!next)
      setLocking(false)
    }).catch(() => { setLocked(!next); setLocking(false) })
  }

  const categoryBorder = () => {
    const border = document.createElement("DIV")
    border.classList.add("category-border")
    border.style = `background: ${info.category_color}`
    return border
  }

  useEffect(() => {
    if (eventRef) {
      // This is because we don't have direct access to the fullcalendar Event component
      eventRef.current.closest('.fc-event').appendChild(categoryBorder())
    }
  }, [eventRef])

  const handleEventClick = e => {
    showModal('#show-workorder-modal')
    jsRequest(workorderModalPath(info))
  }

  const showSameSiteWorkorders = e => {
    e.stopPropagation();
    setFilter(prevFilter =>({
      ...prevFilter,  
      site:           info.site_id, 
      site_name:      info.site,
      text:           '',
      showOpen:       true
    }))
    setState(prevState=>({...prevState, filterOpen: true }))
  }

  const handleMouseEnter = (event) => {
    setShowMarker(true)
    if (externalCard) {
      setState(prevState=>({...prevState, showSmallCard:
        { externalCard: {
            position: {clientX: event.clientX, clientY: event.clientY}, 
            info
          }
        }
      }))
    }
  }

  const handleMouseLeave = () => {
    setShowMarker(false)
    if (externalCard) {
      setState(prevState=>({...prevState, showSmallCard: false }))
    }
  }

  const optimizerLock = info.can_lock_planning && (
    <span
      className     = {`optimizer-lock${locked ? ' is-locked' : ''}`}
      title         = {I18n.t(locked ? 'calendar.optimizer_locked' : 'calendar.optimizer_unlocked')}
      onPointerDown = {swallow}
      onMouseDown   = {swallow}
      onClick       = {toggleLock}
    >
      <FontAwesomeIcon icon={locked ? 'lock' : 'lock-open'} />
    </span>
  )

  return (
    <div onClick={handleEventClick}
         ref          ={eventRef} 
         onMouseEnter ={e=>handleMouseEnter(e)} 
         onMouseLeave ={handleMouseLeave}
    >
      <div className='flexbox-space-between line-one' data-action="application-list#show">
        <b>{info.title}</b>
        <div className="d-flex a-i-center gap-5">
          { info.material_placements?.length > 0 &&
            <i className="fas fa-truck-moving" title={info.material_placements.map(action => action.label).join('\n')}></i>
          }
          { showDuration &&
            <div className="duration"><i className="fas fa-hourglass-half mr-1"></i>{info.duration}h</div>
          }
          { info.is_booking && onRemoveBooking &&
            <span
              className="booking-remove"
              title={I18n.t('button.delete')}
              onClick={e => { e.stopPropagation(); onRemoveBooking(info.booking_id, info.workorder_id) }}
            >
              <FontAwesomeIcon icon='times' />
            </span>
          }
        </div>
      </div>
      <div className="mt-1 line-two flexbox-space-between ">
        <div className="event-address">{info.second_line}</div>
        {/* Pool cards stay two lines high: the appointment markers sit at the
            end of the second line instead of opening a third one. */}
        { externalCard &&
          <div className="d-flex a-i-center gap-5">
            { info.open_appointment && <i className="fas fa-calendar"></i> }
            { info.confirmed_appointment && <FontAwesomeIcon icon='check' /> }
            { info.refused_appointment && <FontAwesomeIcon icon='times' /> }
            { optimizerLock }
          </div>
        }
      </div>
      { !externalCard &&
        <div className="d-flex a-i-center gap-5 event-meta">
          { info.open_appointment && <i className="fas fa-calendar"></i> }
          { info.confirmed_appointment && <FontAwesomeIcon icon='check' /> }
          { info.refused_appointment && <FontAwesomeIcon icon='times' /> }

          { showDate &&
            <div className='mt-1 line-three'>
              {info.event_start} - {info.event_end}
            </div>
          }
          {/* Right after the time range, before the late badge. */}
          { optimizerLock }
          { info.late &&
            <span className="late-flag" title={I18n.t('calendar.late_tooltip')}>
              <i className="fas fa-exclamation-triangle"></i> <span className="late-flag__label">{I18n.t('calendar.late_badge')}</span>
            </span>
          }
        </div>
      }
      { TOOLS.displayOpen == SEPARATED && showMarker && 
        <div className="mr-1 related-workorders"
          onClick={showSameSiteWorkorders}
          onMouseEnter={e => setState(prevState=>({...prevState, showPopup: ({x: e.pageX, y: e.pageY, content: I18n.t('calendar.show_same_site_workorders')})}))}
          onMouseLeave={() => setState(prevState=>({...prevState, showPopup: false }))}
        >
          <i className="fas fa-map-marker-alt"></i>
        </div>
      }
    </div>
  )
}

export default EventCard
